import { useEffect, useState } from "react";
import { useNavigate, Navigate, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { applyMode, initMode } from "@/components/ThemeToggle";

const HERO = "https://images.unsplash.com/photo-1597687228894-111db66403b6?auto=format&fit=crop&w=1400&q=70";
const LOGO = "/nolium-logo-full.png";

export default function Login() {
  const { state, refresh } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [forgot, setForgot] = useState(false);
  const [notice, setNotice] = useState(useLocation().state?.notice || "");
  const [quick, setQuick] = useState(false);
  useEffect(() => { applyMode("dark"); return initMode; }, []);
  useEffect(() => { if (window.location.hostname.endsWith(".preview.emergentagent.com")) { api.get("/auth/quick-login").then((r) => setQuick(r.data.available)).catch(() => {}); } }, []);
  if (state) return <Navigate to="/" replace />;
  const quickLogin = async () => {
    setBusy(true); setErr("");
    try { localStorage.removeItem("company_id"); await api.post("/auth/quick-login"); await refresh(); nav("/"); } catch (e2) { setErr(errMsg(e2)); } finally { setBusy(false); }
  };
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(""); setNotice("");
    try {
      if (forgot) {
        const r = await api.post("/auth/forgot-password", { email });
        setNotice(r.data.message); setForgot(false);
        return;
      }
      localStorage.removeItem("company_id");
      await api.post("/auth/login", { email, password });
      await refresh();
      nav("/");
    } catch (e2) { setErr(errMsg(e2)); } finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr] bg-[#09090B]">
      <div className="relative hidden lg:block overflow-hidden">
        <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#041d33] via-[#083B66]/80 to-[#11A9A2]/30" />
        <div className="absolute bottom-14 left-14 right-14">
          <div className="inline-block bg-white rounded-2xl px-6 py-4 mb-8"><img src={LOGO} alt="NOLIUM" className="h-16 w-auto" data-testid="login-hero-logo" /></div>
          <div className="flex gap-1.5 mb-6"><span className="h-1 w-12 bg-[#083B66]" /><span className="h-1 w-12 bg-[#11A9A2]" /><span className="h-1 w-12 bg-[#A6CE39]" /></div>
          <h1 className="text-5xl xl:text-6xl font-extrabold text-white leading-[1.05]">L'ecosistema digitale<br />del noleggio</h1>
          <p className="text-zinc-200 mt-4 max-w-md">Il gestionale professionale per autonoleggi a breve termine: flotta, prenotazioni, contratti, clienti e incassi in un unico archivio sicuro, separato per ogni società.</p>
        </div>
      </div>
      <div className="flex items-center justify-center p-6">
        <form onSubmit={submit} className="w-full max-w-sm space-y-6 fade-up" data-testid="login-form">
          <div>
            <div className="inline-block bg-white rounded-xl px-4 py-3 mb-6"><img src={LOGO} alt="NOLIUM" className="h-10 w-auto" data-testid="login-logo" /></div>
            <h2 className="text-3xl font-bold text-white">{forgot ? "Password dimenticata" : "Accedi"}</h2>
            <p className="text-sm text-zinc-400 mt-1">{forgot ? "Inserisci l'email del tuo account: ti invieremo un link per reimpostare la password." : "Inserisci le credenziali fornite dal tuo amministratore."}</p>
          </div>
          <div className="space-y-4">
            <div><Label className="text-xs text-zinc-400">{forgot ? "Email" : "Email o nome utente"}</Label><Input type={forgot ? "email" : "text"} autoCapitalize="none" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required className="h-11 mt-1.5" data-testid="login-email-input" /></div>
            {!forgot && <div><div className="flex items-center justify-between"><Label className="text-xs text-zinc-400">Password</Label>
              <button type="button" onClick={() => { setForgot(true); setErr(""); setNotice(""); }} className="text-xs text-[#11A9A2] hover:underline" data-testid="forgot-password-link">Password dimenticata?</button></div>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="h-11 mt-1.5" data-testid="login-password-input" /></div>}
          </div>
          {notice && <div className="text-sm text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-md px-3 py-2" data-testid="login-notice">{notice}</div>}
          {err && <div className="text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-md px-3 py-2" data-testid="login-error">{err}</div>}
          <Button type="submit" disabled={busy} className="w-full h-11 bg-[#11A9A2] hover:bg-[#0e8f89] text-white" data-testid="login-submit-button">
            {busy && <Loader2 className="animate-spin mr-2" size={16} />}{forgot ? "Invia link di ripristino" : "Entra"}
          </Button>
          {forgot && <button type="button" onClick={() => setForgot(false)} className="block text-sm text-[#11A9A2] hover:underline" data-testid="forgot-back-link">Torna all'accesso</button>}
          {quick && !forgot && <div className="border-t border-zinc-800 pt-4">
            <Button type="button" variant="outline" disabled={busy} onClick={quickLogin} className="w-full h-11 border-amber-500/50 text-amber-300 hover:bg-amber-500/10" data-testid="quick-login-superadmin">Accesso rapido Superadmin</Button>
            <p className="text-[11px] text-zinc-500 mt-1.5 text-center">Visibile solo nell'anteprima, mai nell'app pubblicata.</p></div>}
        </form>
      </div>
    </div>
  );
}
